'use client'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Form,
  Button,
  Space,
  Row,
  Col,
  notification,
  Radio,
  Select,
  InputNumber,
  Input
} from 'antd'
import { DatePicker } from 'antd'
import { useTranslation } from 'react-i18next'
import CompaniesComboBox from '../components/Combobox/CompaniesComboBox'
import { useLazyQuery, useMutation } from '@apollo/client'
import { WorklogCreateInput } from '../../../generated/graphql'
import { CREATE_WORKLOG } from '../gql/verteOfficeGQL'
import { getPayload } from '../../../utils/auth'
import DomainCombobox from '../components/Combobox/DomainsGeneralComboBox'
import { generalToObjectiveType } from '../shared/activityTypeMapping'
import { normalizeKey } from '../../shared/verteoffice'
import moment from 'moment'
import { COMPANIES } from '../components/Combobox/ComboboxGQL'
import { GET_DOMAINS } from '../gql/registroHorasGQLignore'
import { DOMAIN_CONFIG } from '../shared/domainConfig'
import { buildDomainLists } from '../shared/buildDomainLists'
import { buildAllObjectivesOptions, ObjectiveOption } from '../shared/objectivesOptions'

// Stable document reference — GET_DOMAINS() uses interpolated template literals
// so gql cannot cache it; calling it inside the component would create a new
// document object on every render, resetting Apollo's useLazyQuery hook state.
const DOMAINS_QUERY = GET_DOMAINS()

type createWorklogInputType = { data: WorklogCreateInput }
type CompanyType = 'compania' | 'prorrateo'

interface CreateTimeRecordFormProps {
  onCancel: () => void
  onClose: () => void
  onBack?: () => void
}

export const CreateTimeRecordForm: React.FC<CreateTimeRecordFormProps> = ({
  onCancel,
  onClose,
  onBack
}) => {
  const [createWorklog, { loading }] = useMutation<any, createWorklogInputType>(
    CREATE_WORKLOG,
    {
      fetchPolicy: 'no-cache'
    }
  )
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const PAYLOAD = getPayload()
  const [companyType, setCompanyType] = useState<CompanyType>('compania')
  const [companyProducts, setCompanyProducts] = useState<string[]>([])
  const [selectedGeneralActivity, setSelectedGeneralActivity] = useState<
    string | undefined
  >()
  const specificActivityType = selectedGeneralActivity
    ? generalToObjectiveType[normalizeKey(selectedGeneralActivity)]
    : undefined

  const [loadDomains, { data: domainsData }] = useLazyQuery(DOMAINS_QUERY, {
    fetchPolicy: 'no-cache',
  })

  const allObjectivesOptions = useMemo<ObjectiveOption[]>(() => {
    if (!domainsData) return []
    return buildAllObjectivesOptions(buildDomainLists(domainsData, DOMAIN_CONFIG))
  }, [domainsData])

  const objectivesLoading = !domainsData

  const [getDefaultCompany] = useLazyQuery(COMPANIES, {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        const company = data?.companiesByUser?.[0]
        if (company?.product) {
          setCompanyProducts(company.product)
          if (company.product.length === 1) {
            form.setFieldsValue({ product: company.product[0] })
          }
        }
      }
    })
  
    useEffect(() => {
      loadDomains()
    if (PAYLOAD?.company) {
        getDefaultCompany({
          variables: {
            user_id: PAYLOAD.id,
            company_id: PAYLOAD.company,
            search: '',
            first: 1,
            last: 0
          }
        })
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

  const handleCompanyFull = (item: {
    id: string
    name: string
    product: string[]
  }) => {
    const products = item.product ?? []
    setCompanyProducts(products)
    form.setFieldsValue({ product: products.length === 1 ? products[0] : undefined })
  }

  const handleCompanyTypeChange = (e: any) => {
    const newType = e.target.value
    setCompanyType(newType)
    if (newType === 'prorrateo') {
      setCompanyProducts([])
      form.setFieldsValue({ company: undefined, product: undefined })
    } else {
      form.setFieldsValue({
        company: { label: PAYLOAD?.company_name, value: PAYLOAD?.company }
      })
    }
  }

  const handleGeneralActivityChange = (value: string) => {
    setSelectedGeneralActivity(value)
    form.setFieldsValue({ specific_activity: undefined, objective: undefined })
  }

  const handleGeneralActivityClear = () => {
    setSelectedGeneralActivity(undefined)
    form.setFieldsValue({ specific_activity: undefined, objective: undefined })
  }

  const handleSpecificActivityChange = (_value: string) => {
    form.setFieldsValue({ objective: undefined })
  }

  const handleObjectiveChange = (_value: string, option: ObjectiveOption | ObjectiveOption[]) => {
    const opt = Array.isArray(option) ? option[0] : option
    if (!opt) {
      return
    }
    setSelectedGeneralActivity(opt.generalActivity)
    form.setFieldsValue({
      activity: opt.generalActivity,
      specific_activity: opt.specificActivity
    })
  }

  const handleSubmit = async (values: any) => {
    const isProrrateo = companyType === 'prorrateo'
    const startTimeIso = values.start_time.toISOString()
    try {
      await createWorklog({
        variables: {
          data: {
            general_activity: values.activity,
            activity_objective: values.objective,
            company_name: isProrrateo ? 'PRORRATEO' : values.company.label,
            company_id: isProrrateo ? 'PRORRATEO' : values.company.value,
            specific_activity: values.specific_activity,
            status: 'COMPLETE',
            start_time: startTimeIso,
            end_time: startTimeIso,
            total_hours: values.total_hours,
            observation: values.observation,
            user_name: `${PAYLOAD?.names} ${PAYLOAD?.lastnames}`,
            user_id: PAYLOAD?.id,
            user_updated: PAYLOAD?.nickname,
            user_created: PAYLOAD?.nickname,
            product: isProrrateo ? 'GENERAL' : values.product
          }
        }
      })

      notification.success({ message: t('worklog_created_successfully') })
      onClose()
    } catch (error: any) {
      notification.error({
        message: t('error_creating_worklog'),
        description: error.message
      })
    } finally {
      form.resetFields()
      setCompanyType('compania')
      setCompanyProducts([])
      setSelectedGeneralActivity(undefined)
    }
  }

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={handleSubmit}
      initialValues={{
        company: { label: PAYLOAD?.company_name, value: PAYLOAD?.company }
      }}
    >
      <Row gutter={[16, 8]}>
        <Col span={24}>
          <Form.Item
            name='activity'
            label={t('main_activity')}
            rules={[{ required: true, message: t('field_required') }]}
            style={{ marginBottom: 8 }}
          >
            <DomainCombobox
              showSearch
              filterOption={false}
              size='small'
              allowClear
              style={{ width: '100%' }}
              placeholder={t('main_activity')}
              type='GENERAL_ACTIVITY'
              onChange={handleGeneralActivityChange}
              onClear={handleGeneralActivityClear}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name='specific_activity'
            label={t('specific_activity')}
            rules={[{ required: true, message: t('field_required') }]}
            style={{ marginBottom: 8 }}
          >
            <DomainCombobox
              showSearch
              filterOption={false}
              size='small'
              allowClear
              style={{ width: '100%' }}
              placeholder={t('specific_activity')}
              type={specificActivityType}
              key={`specific-${specificActivityType}`}
              disabled={!specificActivityType}
              onChange={handleSpecificActivityChange}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label={t('company_type')} style={{ marginBottom: 8 }}>
            <Radio.Group
              value={companyType}
              onChange={handleCompanyTypeChange}
              buttonStyle='solid'
              size='small'
              style={{ width: '100%' }}
            >
              <Radio.Button
                value='compania'
                style={{ width: '50%', textAlign: 'center' }}
              >
                {t('company')}
              </Radio.Button>
              <Radio.Button
                value='prorrateo'
                style={{ width: '50%', textAlign: 'center' }}
              >
                PRORRATEO
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Col>

        {companyType === 'compania' && (
          <Col span={12}>
            <Form.Item
              name='company'
              label={t('company')}
              rules={[{ required: true, message: t('field_required') }]}
              style={{ marginBottom: 8 }}
            >
              <CompaniesComboBox
                showSearch
                filterOption={false}
                labelInValue
                size='small'
                allowClear
                style={{ width: '100%' }}
                placeholder={t('company')}
                user_id={PAYLOAD?.id}
                onSelectFull={handleCompanyFull}
              />
            </Form.Item>
          </Col>
        )}

        {companyType === 'compania' && (
          <Col span={12}>
            <Form.Item
              name='product'
              label={t('product')}
              rules={[{ required: true, message: t('field_required') }]}
              style={{ marginBottom: 8 }}
            >
              <Select
                size='small'
                allowClear
                placeholder={t('product')}
                disabled={companyProducts.length === 0}
                style={{ width: '100%' }}
              >
                {companyProducts.map(p => (
                  <Select.Option key={p} value={p}>
                    {p}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}

        <Col span={24}>
          <Form.Item
            name='objective'
            label={t('objective')}
            rules={[{ required: true, message: t('field_required') }]}
            style={{ marginBottom: 8 }}
          >
            <Select
              showSearch
              allowClear
              size='small'
              style={{ width: '100%' }}
              placeholder={t('objective')}
              loading={objectivesLoading}
              filterOption={(input, option) =>
                String(option?.label ?? '').toUpperCase().includes(input.toUpperCase())
              }
              onChange={handleObjectiveChange}
              options={allObjectivesOptions}
            />
          </Form.Item>
        </Col>

        {/* Campos exclusivos de actividad finalizada */}
        <Col span={12}>
          <Form.Item
            name='start_time'
            label={t('dateFirst')}
            rules={[{ required: true, message: t('field_required') }]}
            style={{ marginBottom: 8 }}
          >
            <DatePicker
              size='small'
              style={{ width: '100%' }}
              disabledDate={current =>
                current && current > moment().endOf('day')
              }
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name='total_hours'
            label={t('hours')}
            rules={[
              { required: true, message: t('field_required') },
              {
                type: 'number',
                min: 0.1,
                max: 2,
                message: 'Mínimo 0.1h, máximo 2h'
              }
            ]}
            style={{ marginBottom: 8 }}
          >
            <InputNumber
              size='small'
              min={0.1}
              max={2}
              step={0.01}
              style={{ width: '100%' }}
              placeholder={t('hours')}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            name='observation'
            label={t('observations')}
            rules={[{ required: true, message: t('field_required') }]}
            style={{ marginBottom: 8 }}
          >
            <Input.TextArea
              size='small'
              rows={3}
              style={{ width: '100%' }}
              placeholder={t('observations')}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 0
            }}
          >
            <Space>
              {onBack && (
                <Button onClick={onBack} size='small' shape='round'>
                  {t('back')}
                </Button>
              )}
              <Button onClick={onCancel} size='small' shape='round' danger>
                {t('cancel')}
              </Button>
              <Button
                type='primary'
                htmlType='submit'
                shape='round'
                size='small'
                loading={loading}
              >
                {t('save')}
              </Button>
            </Space>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  )
}

export default CreateTimeRecordForm
